import { useParams } from 'react-router';
import { Card, Col, Divider, Row } from 'antd';
import dayjs from 'dayjs';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useApplicationQuery from 'hooks/useApplicationQuery';
import {
  formatDateDescriptive,
  formatDateTimeDescriptive,
} from 'utilities/datesAndTimes';

const APPROVED_STATUS = 'APPROVED';

const DocumentURI = ({ document, ...rest }) => {
  document = document || {};
  const displayText = document?.name || 'View Document';
  return (
    <a
      target="_blank"
      rel="noreferrer"
      href={document.uri}
      disabled={!document.uri}
      {...rest}
    >
      {document.uri ? displayText : 'Not Found'}
    </a>
  );
};

const CreditApplicationPage = () => {
  const { applicationId } = useParams();
  const { application, documents, applicationQuery, bureauReports } =
    useApplicationQuery(applicationId);

  const renderSnapshotDocuments = () => {
    if (documents?.snapshots?.length) {
      return documents.snapshots.map((snapshot, index) => {
        const document = {
          uri: snapshot.uri,
          name: snapshot.snapshotType,
        };
        return (
          <div key={`snapshot-${index}`}>
            <DocumentURI
              document={document}
              data-testid={`snapshot-${index}`}
            />
          </div>
        );
      });
    }
    return <DocumentURI data-testid="snapshot-0" />;
  };

  const renderBureauReports = () => {
    if (bureauReports.length) {
      return bureauReports
        .slice()
        .sort((a, b) => dayjs(b.fetchedAt) - dayjs(a.fetchedAt))
        .map((report, index) => {
          return (
            <Row
              gutter={[24, 'var(--spacing-lg)']}
              key={`bureau-report-${index}`}
            >
              <Col md={24} lg={12}>
                <DisplayValue label="Bureau">{report.vendor}</DisplayValue>
              </Col>
              <Col md={24} lg={12}>
                <DisplayValue label="Timestamp">
                  {report.fetchedAt}
                </DisplayValue>
              </Col>
              <Col md={24} lg={12}>
                <DisplayValue label="Pull Type">{report.pullType}</DisplayValue>
              </Col>
              <Col md={24} lg={12}>
                <DisplayValue label="Subscriber Code">
                  {report.subscriberCode}
                </DisplayValue>
              </Col>
              {bureauReports.length > 1 &&
                index != bureauReports.length - 1 && <Divider />}
            </Row>
          );
        });
    }
  };

  return (
    <Card>
      <QueryBoundary query={applicationQuery}>
        <Card
          title={`Credit Application ${formatDateDescriptive(application.submittedAt)}`}
          type="inner"
        >
          <Row gutter={[24, 'var(--spacing-lg)']}>
            <Col md={24} lg={12}>
              <DisplayValue label="Submitted At" canCopy={true}>
                {formatDateTimeDescriptive(application.submittedAt)}
              </DisplayValue>
            </Col>
            <Col md={24} lg={12}>
              <DisplayValue label="Application ID" canCopy={true}>
                {application.applicationId}
              </DisplayValue>
            </Col>
            <Col md={24} lg={12}>
              <DisplayValue label="Status">{application.status}</DisplayValue>
            </Col>
            {application.status !== APPROVED_STATUS && (
              <Col md={24} lg={12}>
                <DisplayValue label="Status Reason">
                  {application.statusReason}
                </DisplayValue>
              </Col>
            )}
            <Col md={24} lg={12}>
              <DisplayValue label="Type">
                {application.applicationType}
              </DisplayValue>
            </Col>
            <Col md={24} lg={12}>
              <DisplayValue label="Source">
                {application.submission?.source}
              </DisplayValue>
            </Col>
            <Col md={24} lg={12}>
              <DisplayValue label="Disclosure Agreement">
                <DocumentURI
                  data-testid="disclosure-agreement"
                  document={documents?.disclosure}
                />
              </DisplayValue>
            </Col>

            <Col md={24} lg={12}>
              <DisplayValue label="Cardholder Agreement (CAG)">
                <DocumentURI
                  data-testid="cardholder-agreement"
                  document={documents?.cag}
                />
              </DisplayValue>
            </Col>

            <Col md={24} lg={12}>
              <DisplayValue label="Adverse Action Notice (AAN)">
                <DocumentURI
                  data-testid="adverse-action-notice"
                  document={documents?.aan}
                />
              </DisplayValue>
            </Col>

            <Col md={24} lg={12}>
              <DisplayValue label="Snapshots">
                {renderSnapshotDocuments()}
              </DisplayValue>
            </Col>
          </Row>
        </Card>
        {bureauReports.length ? (
          <Card
            title="Bureau Reports"
            type="inner"
            style={{ marginTop: 'var(--spacing-sm)' }}
          >
            {renderBureauReports()}
          </Card>
        ) : null}
      </QueryBoundary>
    </Card>
  );
};

export default CreditApplicationPage;
